import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AcceptInvitationPage from './pages/AcceptInvitationPage';
import DashboardPage from './pages/DashboardPage';
import ProjectsPage from './pages/ProjectsPage';
import ProjectPage from './pages/ProjectPage';
import QuickLookPage from './pages/QuickLookPage';
import TenantsPage from './pages/TenantsPage';
import AdminBroadcastsPage from './pages/AdminBroadcastsPage';
import UsersPage from './pages/UsersPage';
import ProfilePage from './pages/ProfilePage';
import AuditLogPage from './pages/AuditLogPage';
import BillingPage from './pages/BillingPage';
import TenantBillingPage from './pages/TenantBillingPage';
import BillingReportsPage from './pages/BillingReportsPage';
import InvoicesPage from './pages/InvoicesPage';
import SettingsPage from './pages/SettingsPage';
import AnalyticsPage from './pages/AnalyticsPage';
import LandingPage from './pages/LandingPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import SupportPage from './pages/SupportPage';
import AdminSupportPage from './pages/AdminSupportPage';
import ContactSubmissionsPage from './pages/ContactSubmissionsPage';
import Sidebar from './components/Sidebar';
import TopBar from './components/TopBar';
import IdleTimeout from './components/IdleTimeout';
import { Toaster } from './components/ui/sonner';

function ProtectedRoute() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-muted-foreground">Loading RoofMate...</p>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return (
    <div className="flex h-screen overflow-hidden" data-testid="app-layout">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar />
        <main className="flex-1 overflow-auto bg-background">
          <Outlet />
        </main>
      </div>
      <IdleTimeout />
    </div>
  );
}

function SuperAdminRoute() {
  const { user } = useAuth();
  if (user?.role !== 'super_admin') return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

function TenantAdminRoute() {
  const { user } = useAuth();
  if (user?.role !== 'tenant_admin') return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

function TenantOrUserRoute() {
  const { user } = useAuth();
  if (user?.role === 'super_admin') return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

function AdminRoute() {
  const { user } = useAuth();
  if (user?.role === 'regular_user') return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

function NotLockedRoute() {
  // Block locked tenants (pending_payment / trial_expired / canceled) AND tenants who
  // have canceled their subscription (cancel_at_period_end OR status=canceled/canceling)
  // from data routes. Super admin is never restricted.
  const { user } = useAuth();
  const isCanceling = !!user?.cancel_at_period_end || ['canceled', 'canceling'].includes(user?.subscription_status);
  if (user?.role !== 'super_admin' && (user?.is_locked || isCanceling)) {
    return <Navigate to={user?.role === 'tenant_admin' ? '/my-billing' : '/dashboard'} replace />;
  }
  return <Outlet />;
}

function AuthRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) {
    // Super admin lands on the dashboard (Quick Look is hidden for them);
    // everyone else lands directly on Quick Look so they can start exploring
    // the last property they were looking at without an extra click.
    const target = user.role === 'super_admin' ? '/dashboard' : '/quick-look';
    return <Navigate to={target} replace />;
  }
  return <LoginPage />;
}

function LandingRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) {
    const target = user.role === 'super_admin' ? '/dashboard' : '/quick-look';
    return <Navigate to={target} replace />;
  }
  return <LandingPage />;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingRedirect />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/login" element={<AuthRedirect />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
            <Route element={<ProtectedRoute />}>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route element={<NotLockedRoute />}>
                <Route path="/analytics" element={<AnalyticsPage />} />
                <Route element={<TenantOrUserRoute />}>
                  <Route path="/projects" element={<ProjectsPage />} />
                  <Route path="/project/:projectId" element={<ProjectPage />} />
                  <Route path="/quick-look" element={<QuickLookPage />} />
                </Route>
              </Route>
              <Route element={<SuperAdminRoute />}>
                <Route path="/tenants" element={<TenantsPage />} />
                <Route path="/billing" element={<BillingPage />} />
                <Route path="/billing-reports" element={<BillingReportsPage />} />
                <Route path="/invoices" element={<InvoicesPage />} />
                <Route path="/admin/support" element={<AdminSupportPage />} />
                <Route path="/admin/contact-submissions" element={<ContactSubmissionsPage />} />
                <Route path="/admin/broadcasts" element={<AdminBroadcastsPage />} />
              </Route>
              <Route element={<TenantAdminRoute />}>
                <Route element={<NotLockedRoute />}>
                  <Route path="/users" element={<UsersPage />} />
                  <Route path="/support" element={<SupportPage />} />
                </Route>
                <Route path="/my-billing" element={<TenantBillingPage />} />
                <Route path="/my-invoices" element={<InvoicesPage />} />
              </Route>
              <Route element={<AdminRoute />}>
                <Route element={<NotLockedRoute />}>
                  <Route path="/audit-logs" element={<AuditLogPage />} />
                </Route>
              </Route>
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
